import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OccupancyHistory, OccupancyLiveMessage, OccupancyOverview, SiteOccupancy } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useLiveChannel } from '../../lib/realtime';
import { useCan } from '../../lib/session';
import { addDays, todayIn } from '../../lib/zoneTime';
import { ErrorPanel } from '../../components/ErrorPanel';
import { ExportButton } from '../../components/ExportButton';
import { CapacityBar, LineChart } from '../../components/phase3';
import { Badge, Card, EmptyState, PageHeader, SelectField, Spinner } from '../../components/ui';
import { useFullSites } from './api';

const overviewKey = ['occupancy', 'overview'] as const;
const LEVEL_TONE: Record<SiteOccupancy['level'], 'neutral' | 'good' | 'warn' | 'bad'> = { none: 'neutral', green: 'good', amber: 'warn', red: 'bad' };
const MAX_POINTS = 120;

export interface Sample {
  at: string;
  count: number;
  capacity: number | null;
}

/** Keeps a long history readable: at most `max` points, each the busiest sample of its stretch (so peaks are never lost). */
export function downsample(samples: readonly Sample[], max = MAX_POINTS): Sample[] {
  if (samples.length <= max) return [...samples];
  const size = Math.ceil(samples.length / max);
  const out: Sample[] = [];
  for (let i = 0; i < samples.length; i += size) {
    const chunk = samples.slice(i, i + size);
    out.push(chunk.reduce((best, s) => (s.count > best.count ? s : best), chunk[0] as Sample));
  }
  return out;
}

function SiteCard({ site, canToday }: { site: SiteOccupancy; canToday: boolean }) {
  const qc = useQueryClient();
  // The server pushes every change on the `occupancy` channel; a missed message is covered by the refetch on reconnect.
  useLiveChannel(
    'occupancy',
    site.siteId,
    (data) => {
      const m = OccupancyLiveMessage.safeParse(data);
      if (!m.success) return;
      if (m.data.type === 'refresh') {
        void qc.invalidateQueries({ queryKey: overviewKey });
        return;
      }
      const next = m.data.site;
      qc.setQueryData<{ sites: SiteOccupancy[] }>(overviewKey, (old) => (old ? { sites: old.sites.map((s) => (s.siteId === next.siteId ? next : s)) } : old));
    },
    () => void qc.invalidateQueries({ queryKey: overviewKey }),
  );
  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {site.siteName}
          <Badge tone={LEVEL_TONE[site.level]}>{t(`occupancy.level.${site.level}`)}</Badge>
        </span>
      }
    >
      <div className="space-y-3">
        <CapacityBar label={t('siteF.occupancy.onSite')} current={site.count} capacity={site.capacity ?? 0} />
        {site.percent !== null ? <p className="text-sm text-slate-700">{t('siteF.occupancy.percent', { n: Math.round(site.percent) })}</p> : <p className="text-sm text-slate-600">{t('siteF.occupancy.noCapacity')}</p>}
        {site.estimated ? <p className="text-xs text-slate-600">{t('siteF.occupancy.estimated', { n: site.assumed })}</p> : null}
        {site.byGroup.length > 0 ? (
          <ul className="space-y-1 text-sm" aria-label={t('siteF.occupancy.byGroup')}>
            {site.byGroup.map((g) => (
              <li key={g.groupId} className="flex justify-between gap-3">
                <span>{g.groupName}</span>
                <span className="tabular-nums">{g.count}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {canToday ? (
          <Link className="text-sm font-medium text-brand hover:underline" to="/today">
            {t('siteF.occupancy.seeToday')}
          </Link>
        ) : null}
      </div>
    </Card>
  );
}

const RANGES = [1, 7, 30] as const;

function History({ sites }: { sites: SiteOccupancy[] }) {
  const fullSites = useFullSites();
  const [siteId, setSiteId] = useState('');
  const [days, setDays] = useState<(typeof RANGES)[number]>(1);
  const effective = siteId || sites[0]?.siteId || '';
  const zone = fullSites.data?.items.find((s) => s.id === effective)?.timeZone ?? 'Europe/London';
  const to = addDays(todayIn(zone), 1);
  const from = addDays(todayIn(zone), 1 - days);
  const q = useQuery({
    queryKey: ['occupancy', 'history', effective, from, to],
    enabled: effective !== '',
    queryFn: () => api.get('/occupancy/history', { query: { siteId: effective, from, to }, schema: OccupancyHistory }),
  });
  const label = (iso: string): string =>
    new Intl.DateTimeFormat('en-GB', days === 1 ? { hour: '2-digit', minute: '2-digit', timeZone: zone } : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: zone }).format(new Date(iso));
  const samples = downsample(q.data?.samples ?? []);
  const capacity = [...samples].reverse().find((s) => s.capacity !== null)?.capacity ?? null;
  return (
    <Card title={t('siteF.occupancy.history')} description={t('siteF.occupancy.historyHint')}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <SelectField label={t('siteF.site')} value={effective} onChange={(e) => setSiteId(e.target.value)}>
            {sites.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.siteName}
              </option>
            ))}
          </SelectField>
          <SelectField label={t('siteF.occupancy.range')} value={String(days)} onChange={(e) => setDays(Number(e.target.value) as (typeof RANGES)[number])}>
            {RANGES.map((d) => (
              <option key={d} value={d}>
                {t(`siteF.occupancy.range.${d}`)}
              </option>
            ))}
          </SelectField>
          <ExportButton path="/occupancy/history/export" query={{ siteId: effective, from, to }} fileName="occupancy" disabled={effective === ''} />
        </div>
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <LineChart
            title={t('siteF.occupancy.history')}
            series={[{ name: t('siteF.occupancy.onSite'), points: samples.map((s) => ({ x: label(s.at), y: s.count })) }]}
            {...(capacity ? { limit: { value: capacity, label: t('siteF.occupancy.capacityLine', { n: capacity }) } } : {})}
          />
        )}
      </div>
    </Card>
  );
}

/** Portal page "Live occupancy" (occupancy.md section 6): one card per site with a bar that follows the count, and a history chart. Counts only, never names. */
export function OccupancyPage() {
  const canToday = useCan('today.view');
  const q = useQuery({ queryKey: overviewKey, queryFn: () => api.get('/occupancy', { schema: OccupancyOverview }) });
  return (
    <>
      <PageHeader title={t('siteF.occupancy.title')} description={t('siteF.occupancy.description')} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data.sites.length === 0 ? (
        <EmptyState message={t('siteF.occupancy.empty')} />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {q.data.sites.map((s) => (
              <SiteCard key={s.siteId} site={s} canToday={canToday} />
            ))}
          </div>
          <History sites={q.data.sites} />
        </div>
      )}
    </>
  );
}
